<template>
  <div :class="ns.e('header')" :style="headerStyle">
    <div :class="[ns.e('row'), ns.is('header-row', true)]" :style="tableRowStyle">
      <grid-header-cell
        v-for="col in columns"
        :key="col.id"
        :column="col"
        :cell-style="headerCellStyle(col)"
        :class="{ 'fixed--hidden': isColumnHidden(col) }"
        @sort="onSort(col, $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import { addUnit } from '@zoryn/utils'
import {
  getColumnAlignStyle,
  resolveColumnMinWidth,
  resolveColumnWidth,
} from '../column/meta'
import { isFixedColumnHidden } from '../utils/fixed-column'
import GridHeaderCell from './grid-header-cell.vue'
import type { CSSProperties } from 'vue'
import type { DataGrid, GridColumnInfo } from '../types'
import type { GridFixedType } from '../utils/fixed-column'

const props = defineProps<{
  fixedType: GridFixedType
  overflowX: boolean
  columns: GridColumnInfo[]
  contentWidth?: number
}>()

const ns = useNamespace('grid')
const $grid = inject('$grid') as DataGrid

const columns = computed(() => props.columns)
const tableWidth = computed(() =>
  columns.value.reduce((sum, col) => sum + resolveColumnWidth(col), 0)
)
const resolvedContentWidth = computed(
  () => props.contentWidth ?? tableWidth.value
)
const headerStyle = computed<CSSProperties>(() => {
  const width = resolvedContentWidth.value
  if (width <= 0) {
    return {}
  }
  return {
    width: `${width}px`,
    minWidth: `${width}px`,
    maxWidth: `${width}px`,
    overflow: 'hidden',
  }
})
const tableRowStyle = computed<CSSProperties>(() => ({
  minWidth: '100%',
  width: `${resolvedContentWidth.value}px`,
}))

function isColumnHidden(col: GridColumnInfo) {
  return isFixedColumnHidden(
    col,
    props.fixedType,
    props.overflowX,
    $grid.internalData.visibleColumns
  )
}

function headerCellStyle(col: GridColumnInfo): CSSProperties {
  const w = resolveColumnWidth(col)
  const style: CSSProperties = {
    width: addUnit(w),
    minWidth: addUnit(resolveColumnMinWidth(col)),
    flex: `0 0 ${addUnit(w)}`,
    ...getColumnAlignStyle(col.align),
  }
  if (col.type !== 'checkbox') {
    style.textAlign = 'center'
    style.justifyContent = 'center'
  }
  return style
}

function onSort(col: GridColumnInfo, order: 'asc' | 'desc') {
  $grid.handleSort(col, order)
}
</script>
